"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useHomepageDriverSession } from "@/lib/homepageDriverSession";

type Props = {
  category: string;
  compact?: boolean;
};

type Coordinates = {
  latitude: number;
  longitude: number;
};

export default function ApprovedDriverAvailability({ category, compact = false }: Props) {
  const endpoint =
    category === "Goods"
      ? "/api/goods-transport/driver-availability"
      : "/api/driver-availability";

  const [online, setOnline] = useState(false);

  const [driverVehicle, setDriverVehicle] = useState<{
    category: string;
    vehicleType: string;
    vehicleModel: string;
    vehicleNumber: string;
    status: string;
  } | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    async function loadVehicle() {
      try {
        const response = await fetch("/api/driver-registration", {
          cache: "no-store",
          signal: controller.signal,
        });
        if (!response.ok) return;

        const data = await response.json();
        if (!controller.signal.aborted) {
          setDriverVehicle(data.registration ?? null);
        }
      } catch {
        // Keep the existing availability controls working.
      }
    }

    void loadVehicle();
    return () => controller.abort();
  }, []);

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const onlineRef = useRef(false);
  const busyRef = useRef(false);
  const latestPosition = useRef<Coordinates | null>(null);
  const latestPositionAt = useRef<number>(0);
  // Serialize availability writes so an older Online heartbeat
  // cannot arrive at the server after a newer Offline request.
  const availabilityQueue = useRef<Promise<void>>(Promise.resolve());
  // Kept only in this mounted component; never stored in localStorage.
  const availabilitySessionToken = useRef<string | null>(null);
  const sharedDriverSession = useHomepageDriverSession();

  const updateAvailability = useCallback(
    (available: boolean, position?: Coordinates) => {
      // Capture the intended action before the request enters the queue.
      const action = available
        ? onlineRef.current ? "heartbeat" : "start"
        : "stop";
      const operation = availabilityQueue.current.then(async () => {
        const response = await fetch(endpoint, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
          available,
          action,
          availabilitySessionToken:
            sharedDriverSession?.sessionRef.current?.userId === sharedDriverSession?.userId &&
            sharedDriverSession?.sessionRef.current?.category === category
              ? sharedDriverSession.sessionRef.current.token
              : sharedDriverSession
                ? null
                : availabilitySessionToken.current,
          ...position,
        }),
        });

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${typeof data.message === "string" ? data.message : "Unable to update availability."}`);
        }

        if (
          available &&
          typeof data.availabilitySessionToken === "string" &&
          data.availabilitySessionToken.length > 0
        ) {
          availabilitySessionToken.current = data.availabilitySessionToken;
            if (sharedDriverSession?.userId != null) {
              sessionStorage.setItem(`apnasaathi:driver-online:${sharedDriverSession.userId}:${category}`, data.availabilitySessionToken);
            }

          if (sharedDriverSession?.userId != null) {
            sharedDriverSession.sessionRef.current = {
              userId: sharedDriverSession.userId,
              category: category === "Goods" ? "Goods" : "Passenger",
              token: data.availabilitySessionToken,
            };
          }
        } else if (!available) {
          availabilitySessionToken.current = null;
            if (sharedDriverSession?.userId != null) {
              sessionStorage.removeItem(`apnasaathi:driver-online:${sharedDriverSession.userId}:${category}`);
            }

          if (
            sharedDriverSession?.sessionRef.current?.userId ===
              sharedDriverSession?.userId &&
            sharedDriverSession?.sessionRef.current?.category === category
          ) {
            sharedDriverSession.sessionRef.current = null;
          }
        }

        return data;
      });

      // Preserve the caller's error, while keeping the queue usable
      // if an earlier request fails.
      availabilityQueue.current = operation.then(
        () => undefined,
        () => undefined
      );

      return operation;
    },
    [endpoint, category, sharedDriverSession]
  );

  useEffect(() => {
    const controller = new AbortController();

    async function load() {
      setLoading(true);
      setError("");

      try {
        const response = await fetch(endpoint, {
          cache: "no-store",
          signal: controller.signal,
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || "Unable to load availability.");
        }

        if (controller.signal.aborted) return;

        const available =
          category === "Goods"
            ? data.available === true
            : data.driverAvailableNow === true;

          const ownedSession =
            sharedDriverSession?.sessionRef.current?.userId === sharedDriverSession?.userId &&
            sharedDriverSession?.sessionRef.current?.category === category &&
            Boolean(sharedDriverSession?.sessionRef.current?.token);

          const sessionKey =
            sharedDriverSession?.userId != null
              ? `apnasaathi:driver-online:${sharedDriverSession.userId}:${category}`
              : null;

          const savedToken = ownedSession
            ? sharedDriverSession!.sessionRef.current!.token
            : sessionKey
              ? sessionStorage.getItem(sessionKey)
              : null;

          let canResume = false;

          if (savedToken && sharedDriverSession?.userId != null) {
            try {
              const position = await new Promise<GeolocationPosition>((resolve, reject) => {
                navigator.geolocation.getCurrentPosition(resolve, reject, {
                  enableHighAccuracy: true,
                  maximumAge: 0,
                  timeout: 15000,
                });
              });

              if (controller.signal.aborted) return;

              const heartbeat = await fetch(endpoint, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  available: true,
                  action: "heartbeat",
                  availabilitySessionToken: savedToken,
                  latitude: position.coords.latitude,
                  longitude: position.coords.longitude,
                }),
                signal: controller.signal,
              });

              if (controller.signal.aborted) return;

              if (heartbeat.ok) {
                canResume = true;
                availabilitySessionToken.current = savedToken;
                sharedDriverSession.sessionRef.current = {
                  userId: sharedDriverSession.userId,
                  category: category === "Goods" ? "Goods" : "Passenger",
                  token: savedToken,
                };
                latestPosition.current = {
                  latitude: position.coords.latitude,
                  longitude: position.coords.longitude,
                };
                latestPositionAt.current = Date.now();
              } else if (heartbeat.status === 409 || heartbeat.status === 401) {
                if (sessionKey) sessionStorage.removeItem(sessionKey);
              }
            } catch {
              if (!controller.signal.aborted) {
                setError("Fresh GPS verification failed. Press Go Online to retry.");
              }
            }
          }

          if (controller.signal.aborted) return;

          onlineRef.current = canResume;
          setOnline(canResume);

          window.dispatchEvent(new CustomEvent("apnasaathi:driver-availability-changed", {
            detail: { category, online: canResume },
          }));

          if (canResume) {
            setError("");
          } else if (available && !savedToken) {
            setError("Previous session found. Press Go Online to start a fresh GPS session.");
          }
      } catch (cause) {
        if (!controller.signal.aborted) {
          setError(
            cause instanceof Error ? cause.message : "Unable to load availability."
          );
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }

    void load();
    return () => controller.abort();
  }, [category, endpoint, sharedDriverSession]);

  async function toggle() {
    if (busyRef.current || loading) return;

    busyRef.current = true;
    setBusy(true);
    setError("");

    try {
      if (onlineRef.current) {
        await updateAvailability(false);
        onlineRef.current = false;
        latestPosition.current = null;
        latestPositionAt.current = 0;
        setOnline(false);
        window.dispatchEvent(new CustomEvent("apnasaathi:driver-availability-changed", {
          detail: { category, online: false },
        }));
        return;
      }

      if (!navigator.geolocation) {
        throw new Error("GPS is not available in this browser.");
      }

      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          maximumAge: 0,
          timeout: 15000,
        });
      });

      const point = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
      };

      await updateAvailability(true, point);

      latestPosition.current = point;
      latestPositionAt.current = Date.now();
      onlineRef.current = true;
      setOnline(true);

      window.dispatchEvent(new CustomEvent("apnasaathi:driver-availability-changed", {
        detail: { category, online: true },
      }));

      if (category === "Passenger") {
        window.dispatchEvent(new Event("apnasaathi:passenger-driver-online"));
      }
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "Unable to change availability."
      );
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }

  useEffect(() => {
    if (!online) return;

    let active = true;
    let sending = false;
    let stopping = false;

    async function stopForLostGps(message: string) {
      if (!active || stopping || !onlineRef.current) return;

      stopping = true;
      latestPosition.current = null;
      latestPositionAt.current = 0;
      setError(message);

      try {
        await updateAvailability(false);

        if (active) {
          onlineRef.current = false;
          setOnline(false);
        }
      } catch {
        if (active) {
          setError(message + " Could not confirm Offline. Please retry manually.");
        }
      } finally {
        stopping = false;
      }
    }

    if (!navigator.geolocation) {
      void stopForLostGps("GPS is unavailable. Switching Offline.");
      return;
    }

    // Follow the working /ride-requests approach: send GPS updates
    // promptly, but throttle network heartbeats to at most one per 10s.
    let lastLocationHeartbeatAt = 0;

    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        if (!active) return;
        latestPosition.current = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        };
        latestPositionAt.current = Date.now();

        const now = Date.now();
        if (now - lastLocationHeartbeatAt >= 10000) {
          lastLocationHeartbeatAt = now;
          void heartbeat();
        }
      },
      (gpsError) => {
          if (!active) return;

          if (gpsError.code === gpsError.PERMISSION_DENIED) {
            void stopForLostGps("GPS permission denied. Switching Offline.");
            return;
          }

          // Temporary GPS errors retain the last valid location.
          // Heartbeat retries GPS and checks the 45-second freshness limit.
          setError("GPS temporarily unavailable. Retrying location...");
        },
      {
        enableHighAccuracy: true,
        maximumAge: 5000,
        timeout: 15000,
      }
    );

    async function heartbeat() {
      if (!active || sending || stopping || busyRef.current || !onlineRef.current) return;

      let point = latestPosition.current;

        if (!point || Date.now() - latestPositionAt.current > 20000) {
          try {
            const position = await new Promise<GeolocationPosition>((resolve, reject) => {
              navigator.geolocation.getCurrentPosition(resolve, reject, {
                enableHighAccuracy: true,
                maximumAge: 0,
                timeout: 15000,
              });
            });

            if (!active || !onlineRef.current) return;

            point = {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            };
            latestPosition.current = point;
            latestPositionAt.current = Date.now();
          } catch {
            // Never refresh the timestamp using an outdated GPS reading.
          }
        }

        if (!point || Date.now() - latestPositionAt.current > 45000) {
          if (active) {
            latestPosition.current = null;
            latestPositionAt.current = 0;
            setError(
              "GPS temporarily unavailable. Reconnecting... " +
              "Live location is paused; accepted trips remain accessible."
            );
          }
          // Never heartbeat using stale coordinates.
          // Do not issue an automatic Offline write for a temporary GPS loss.
          // Server freshness and session-expiry checks remain authoritative.
          return;
        }

      sending = true;

      try {
        await updateAvailability(true, point);
        if (active) setError("");
      } catch (cause) {
        const expiredSession =
          category === "Passenger" &&
          cause instanceof Error &&
          cause.message.startsWith("HTTP 409:");

        if (active && expiredSession && point &&
            Date.now() - latestPositionAt.current <= 45000) {
          try {
            // The server resumes only an expired session for a DRIVER
            // still marked Online; it never replaces an active token.
            const response = await fetch(endpoint, {
              method: "PATCH",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                available: true,
                action: "resume",
                latitude: point.latitude,
                longitude: point.longitude,
              }),
            });

            const data = await response.json().catch(() => ({}));

            if (!response.ok) {
              throw new Error(
                typeof data.message === "string"
                  ? data.message
                  : "DRIVER session recovery rejected."
              );
            }

            if (!active || !onlineRef.current) return;

            if (typeof data.availabilitySessionToken !== "string") {
              throw new Error("Resume did not return a session token.");
            }

            availabilitySessionToken.current = data.availabilitySessionToken;

            if (sharedDriverSession?.userId != null) {
              sharedDriverSession.sessionRef.current = {
                userId: sharedDriverSession.userId,
                category: "Passenger",
                token: data.availabilitySessionToken,
              };
              sessionStorage.setItem(
                `apnasaathi:driver-online:${sharedDriverSession.userId}:Passenger`,
                data.availabilitySessionToken
              );
            }

            setError("");
          } catch (recoveryError) {
            if (active) {
              setError(
                recoveryError instanceof Error
                  ? recoveryError.message
                  : "DRIVER session recovery failed."
              );
            }
          }
        } else if (active) {
          setError(
            cause instanceof Error ? cause.message : "Online heartbeat failed."
          );
        }
      } finally {
        sending = false;
      }
    }

    const timer = window.setInterval(() => {
      void heartbeat();
    }, 30000);

    // Background tabs may have paused timers. Retry promptly on return.
    let lastWakeAttempt = 0;
    const resumeHeartbeat = () => {
      if (document.visibilityState !== "visible") return;
      const now = Date.now();
      if (now - lastWakeAttempt < 3000) return;
      lastWakeAttempt = now;
      void heartbeat();
    };

    document.addEventListener("visibilitychange", resumeHeartbeat);
    window.addEventListener("focus", resumeHeartbeat);

    // Retry Passenger DRIVER heartbeat immediately when internet returns.
    const handleNetworkOnline = () => {
      if (category === "Passenger") resumeHeartbeat();
    };
    window.addEventListener("online", handleNetworkOnline);

    return () => {
      active = false;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", resumeHeartbeat);
      window.removeEventListener("focus", resumeHeartbeat);
      window.removeEventListener("online", handleNetworkOnline);
      navigator.geolocation.clearWatch(watchId);
    };
  }, [online, updateAvailability]);

  return (
    <div className={compact ? "relative flex h-16 items-center justify-center" : "space-y-3"}>
      <div className={compact ? "flex h-full items-center justify-center" : "flex items-center justify-between gap-3"}>
        <div className={compact ? "hidden" : ""}>
          <p className="font-semibold text-slate-900">
              {category === "Goods" ? "🚚 DRIVER" : "🚗 DRIVER"}
            </p>
        </div>

        <button
          type="button"
          role="switch"
          aria-checked={online}
          aria-label={online ? "Go Offline" : "Go Online"}
          onClick={() => void toggle()}
          disabled={loading || busy}
          className={
            compact
              ? "w-[102px] rounded-xl border border-orange-200 bg-orange-50 px-1.5 py-1.5 text-[11px] font-bold leading-tight shadow-sm transition disabled:opacity-50 sm:w-[125px]"
              : `rounded-xl px-4 py-3 text-sm font-bold text-white disabled:opacity-50 ${
                  online ? "bg-rose-600" : "bg-emerald-700"
                }`
          }
        >
          {compact ? (
            <span className="flex flex-col items-center justify-center gap-0.5">
              <span className="text-[11px] font-extrabold text-slate-800">
                Rider
              </span>
              <span className={`w-full rounded-full px-1 py-1 ${
                online
                  ? "bg-emerald-100 text-emerald-800"
                  : "bg-slate-200 text-slate-700"
              }`}>
                {online ? "🟢 Online" : "⚪ Offline"}
              </span>
            </span>
          ) : loading ? (
            "Loading..."
          ) : busy ? (
            "Please wait..."
          ) : online ? (
            "Go Offline"
          ) : (
            "Go Online"
          )}
        </button>

      </div>

      {!compact && online && driverVehicle?.status === "Approved" &&
        driverVehicle.category === category && (
          <div className={compact
            ? "absolute right-full top-9 mr-2 w-32 text-center leading-tight"
            : "space-y-1 rounded-lg bg-white/90 px-2 py-2"
          }>
            <p className={compact
              ? "truncate text-[9px] font-medium text-slate-700"
              : "text-xs font-semibold text-slate-800"
            }>
              {driverVehicle.vehicleType} · {driverVehicle.vehicleModel}
            </p>
            <p className={compact
              ? "text-[9px] text-slate-500"
              : "text-[11px] text-slate-600"
            }>
              {driverVehicle.vehicleNumber}
            </p>
            {!compact && (
              <p className="text-xs font-semibold text-emerald-700">
                {category === "Passenger"
                  ? "📡 Live Radar — checking Passenger requests"
                  : "🚚 Goods DRIVER Online"}
              </p>
            )}
          </div>
        )}
      {error && (
        <p
          role="alert"
          className={
            compact
              ? "absolute right-0 top-full z-[1200] mt-2 max-h-28 w-64 max-w-[90vw] overflow-y-auto rounded-xl border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900 shadow-lg"
              : "rounded-lg bg-amber-50 p-3 text-sm text-amber-900"
          }
        >
          {error}
        </p>
      )}
    </div>
  );
}
